import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import type { Task } from '@/types/task';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface TasksCalendarViewProps {
  readonly tasks: Task[];
  readonly loading?: boolean;
  readonly onTaskClick: (task: Task) => void;
  readonly onTaskUpdate: (task: Task) => void;
}

export function TasksCalendarView({ tasks, loading, onTaskClick, onTaskUpdate }: Readonly<TasksCalendarViewProps>) {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());

  const getTaskTypeColor = (type: string) => {
    switch (type) {
      case 'General Task': return 'bg-blue-50 border-blue-200 hover:bg-blue-100';
      case 'Inspection': return 'bg-purple-50 border-purple-200 hover:bg-purple-100';
      case 'Maintenance Request': return 'bg-orange-50 border-orange-200 hover:bg-orange-100';
      case 'Documentation Update': return 'bg-teal-50 border-teal-200 hover:bg-teal-100';
      case 'Verification': return 'bg-green-50 border-green-200 hover:bg-green-100';
      case 'Follow-up Action': return 'bg-amber-50 border-amber-200 hover:bg-amber-100';
      case 'Data Collection': return 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100';
      default: return 'bg-gray-50 border-gray-200 hover:bg-gray-100';
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const navigateToToday = () => {
    setCurrentDate(new Date());
  };

  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const getTasksForDate = (date: Date | null) => {
    if (!date || !tasks) return [];
    
    return tasks.filter(task => {
      if (!task.dueDate) return false;
      const taskDate = new Date(task.dueDate);
      return (
        taskDate.getDate() === date.getDate() &&
        taskDate.getMonth() === date.getMonth() &&
        taskDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const isToday = (date: Date | null) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const monthDays = getMonthDays();
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="h-full overflow-auto p-6">
        <Skeleton className="h-12 mb-4" />
        <div className="grid grid-cols-7 gap-2">
          {[...Array(35)].map((_, i) => (
            <Skeleton key={`skeleton-${i}`} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Calendar Controls */}
      <div className="p-6 shrink-0">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">{monthName}</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={navigateToToday}>
              Today
            </Button>
            <Button variant="outline" size="icon" onClick={() => navigateMonth('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={() => navigateMonth('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="border rounded-lg overflow-hidden bg-white dark:bg-background">
          {/* Day Headers */}
          <div className="grid grid-cols-7 bg-muted border-b">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="p-3 text-center text-sm font-medium border-r last:border-r-0">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 bg-white dark:bg-background">
            {monthDays.map((date, index) => {
              const dayTasks = getTasksForDate(date);
              const isCurrentDay = isToday(date);

              return (
                <div
                  key={`day-${index}`}
                  className={cn(
                    'min-h-[120px] border-r border-b p-2',
                    !date && 'bg-muted/30',
                    isCurrentDay && 'bg-primary/5'
                  )}
                >
                  {date ? (
                    <>
                      <div className={cn(
                        'text-sm font-medium mb-2',
                        isCurrentDay && 'text-primary'
                      )}>
                        {date.getDate()}
                      </div>
                      <div className="space-y-1">
                        {dayTasks.slice(0, 3).map((task) => (
                          <button
                            type="button"
                            key={task.id}
                            className={cn(
                              'text-xs p-1.5 rounded cursor-pointer hover:shadow-sm transition-shadow border-l-3 w-full text-left',
                              getTaskTypeColor(task.type)
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/tasks/${task.id}`);
                            }}
                          >
                            <div className="font-medium truncate">{task.title}</div>
                            <div className="flex items-center gap-1 text-muted-foreground mt-0.5">
                              {task.assignee?.type === 'user' && (
                                <Avatar className="h-3 w-3">
                                  <AvatarImage src={task.assignee.avatar} />
                                  <AvatarFallback className="text-[8px]">
                                    {task.assignee.name.split(' ').map(n => n[0]).join('')}
                                  </AvatarFallback>
                                </Avatar>
                              )}
                              {task.assignee?.type === 'group' && (
                                <Users className="h-3 w-3" />
                              )}
                              {task.assignee && (
                                <span className="truncate text-[10px]">{task.assignee.name}</span>
                              )}
                            </div>
                          </button>
                        ))}
                        {dayTasks.length > 3 && (
                          <div className="text-xs text-muted-foreground text-center py-1">
                            +{dayTasks.length - 3} more
                          </div>
                        )}
                      </div>
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
